import axios from 'axios'
import { Message } from '@arco-design/web-vue'
import { useSysStore, useUserStore } from '@/store'
import i18n from '@/locales'
import { whitePathList } from '@/router/permission'
import router from '@/router'

// 获取全局 t 函数
const { t } = i18n.global

// 使用独立实例，避免 HMR 时拦截器重复注册导致响应被解包多次
const service = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://42.193.239.46:9000',
  timeout: 30000
})

// 标记是否有错误弹窗显示中,避免重复弹窗
let errorVisible = false

// 标记是否正在处理登录失效（401或掉线），用于阻止其他错误提示
let isHandlingAuthError = false

// 记录最后一次显示错误的时间戳，用于防止短时间内重复显示
let lastErrorTime = 0
const ERROR_COOLDOWN = 3000 // 错误提示冷却时间：3秒

// 记录已显示的错误消息（用于去重）
const shownErrors = new Map<string, number>()

// 导出标记变量，供其他模块使用
export const getAuthErrorStatus = () => isHandlingAuthError

// 显示错误提示的统一函数（带去重）
function showErrorMessage(content: string, duration = 3000, force = false) {
  // 如果正在处理登录失效，除非强制显示，否则不显示其他错误提示
  if (isHandlingAuthError && !force) {
    return
  }

  const now = Date.now()
  const lastShown = shownErrors.get(content)

  // 如果相同内容在冷却时间内已经显示过，则跳过
  if (lastShown && now - lastShown < ERROR_COOLDOWN) {
    return
  }

  // 记录此消息的显示时间
  shownErrors.set(content, now)
  lastErrorTime = now

  // 显示消息
  Message.error({
    content,
    duration
  })

  // 清理过期的记录（避免内存泄漏）
  setTimeout(() => {
    shownErrors.delete(content)
  }, ERROR_COOLDOWN)
}

// 登录相关请求：不受 isHandlingAuthError / 白名单 401 静默影响
function isLoginRequest(config?: { url?: string }) {
  const url = config?.url || ''
  return (
    url.includes('/login') ||
    url.includes('bind-phone-login') ||
    url.includes('change-phone-bind-login')
  )
}

// 登录业务错误提示（优先前端 i18n）
function showLoginBizError(error: any) {
  const data = error.response?.data || {}
  const errorCode = data.code
  const errorMessage = data.message || data.msg

  if (errorCode === 'AUTHENTICATION_FAILURE') {
    showErrorMessage(t('login.invalidPassword'), 3000, true)
  } else if (errorCode === 'USER_NOT_EXIST') {
    showErrorMessage(t('login.studentIdNotFound'), 3000, true)
  } else if (errorMessage) {
    showErrorMessage(errorMessage, 3000, true)
  } else {
    showErrorMessage(t('login.loginFailed'), 3000, true)
  }
}

// 租户代码,可从环境变量读取
const tenantCode = import.meta.env.VITE_TENANT_CODE || 'STUDENT'

service.interceptors.request.use(
  config => {
    const userStore = useUserStore()
    config.headers.Tenant = tenantCode
    config.headers.tenantCode = tenantCode
    const token = userStore.token
    if (config.headers && token) {
      config.headers.Authorization = 'Bearer ' + token
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

service.interceptors.response.use(
  response => {
    // 响应白名单,这些接口返回原始response对象
    const whiteList: string[] = []
    if (whiteList.find(item => response.request.responseURL.includes(item))) {
      return response
    }
    return response.data
  },
  error => {
    console.log(error, 'res')

    const res = error.response?.data
    const status = error.response?.status

    // 登录类请求优先展示业务错误，避免被 401 静默或 isHandlingAuthError 吞掉
    if (isLoginRequest(error.config)) {
      showLoginBizError(error)
      return Promise.reject(error)
    }

    // 检查是否是白名单路由
    // FIXME 没处理云点播的情况
    const isGuestRoute = whitePathList.includes(location.pathname) || router.currentRoute.value.meta.requiresAuth === false
    const hasToken = !!useUserStore().token
    if ([401, 403].includes(status as number) && isGuestRoute && !hasToken) {
      return Promise.reject(error)
    }
    // 检查是否是异地登录或被踢下线
    const isRemoteLogin =
      res?.code === 'REMOTE_LOGIN' ||
      res?.code === 'KICKED_OUT' ||
      res?.code === 'USER_KICKED' ||
      (res?.message && (
        res.message.includes('异地') ||
        res.message.includes('掉线') ||
        res.message.includes('其他地方登录') ||
        res.message.includes('已在其他')
      ))

    if (isRemoteLogin) {
      // 立即设置标记
      isHandlingAuthError = true
      ;(error as any).isAuthError = true // 添加特殊标记

      if (!errorVisible) {
        errorVisible = true

        useUserStore().clearCache()
        showErrorMessage(res?.message || '您的账号已在其他地方登录', 3000, true) // 强制显示

        setTimeout(() => {
          if(useSysStore().isInMp) {
            wx.miniProgram.reLaunch({
              url: '/pages/login/index'
            })
          } else {
            window.location.href = '/login'
            errorVisible = false
            // 延迟重置标记，确保在跳转期间不显示其他错误
            setTimeout(() => {
              isHandlingAuthError = false
            }, 3000)
          }

        }, 2000)
      }
      return Promise.reject(error)
    }

    // 处理401未授权
    if (status === 401) {
      // 立即设置标记，阻止后续的所有错误提示
      isHandlingAuthError = true

      // 给错误对象添加特殊标记，让页面可以识别
      ;(error as any).isAuthError = true

      if (!errorVisible) {
        errorVisible = true
        const hasToken = !!useUserStore().token
        useUserStore().clearCache()
        if(useSysStore().isInMp) {
          wx.miniProgram.reLaunch({
            url: '/pages/login/index'
          })
        } else if (window.location.pathname !== '/login') {
          hasToken && showErrorMessage(t('message.loginExpired'), 3000, true) // 强制显示
          setTimeout(() => {
            if(window.location.pathname === '/login') {
              return
            }
            window.location.href = '/login'
            errorVisible = false
            // 延迟重置标记，确保在跳转期间不显示其他错误
            setTimeout(() => {
              isHandlingAuthError = false
            }, 3000) // 延长到3秒，确保跳转完成
          }, 2000)
        } else {
          errorVisible = false
          // 即使在登录页，也要延迟重置标记
          setTimeout(() => {
            isHandlingAuthError = false
          }, 1000)
        }
      }
      return Promise.reject(error)
    }

    // 如果正在处理认证错误，标记所有后续错误
    if (isHandlingAuthError) {
      ;(error as any).isAuthError = true
      return Promise.reject(error)
    }

    // 处理网络错误
    if (error.code === 'ERR_NETWORK') {
      showErrorMessage(t('message.networkError'))
      return Promise.reject(error)
    }

    // 处理超时
    if (error.code === 'ECONNABORTED') {
      showErrorMessage(t('message.requestTimeout'))
      return Promise.reject(error)
    }

    console.error(error)

    // 错误码映射 - 将后端错误码转换为前端国际化提示
    const errorCode = error.response?.data?.code
    const errorMessage = error.response?.data?.message

    // 根据错误码显示对应的国际化提示
    if (errorCode === 'AUTHENTICATION_FAILURE') {
      // 账号或密码错误
      showErrorMessage(t('login.invalidPassword'))
    } else if (errorCode === 'USER_NOT_EXIST') {
      // 学号不存在
      showErrorMessage(t('login.studentIdNotFound'))
    } else if (errorMessage) {
      // 其他错误显示后端返回的消息
      showErrorMessage(errorMessage)
    } else {
      // 默认错误提示
      showErrorMessage(t('message.requestFailed') || '请求失败')
    }

    return Promise.reject(error)
  }
)

// 导出axios实例
export default service
